import { useEffect, useRef, useState } from 'preact/hooks';
import * as st from '../state/store';
import { BY_ID, REGION_LABEL, sideLabel } from '../state/structures';
import { LAYER_BY_ID } from '../viewer/layers';
import { loadCard, peekCard, type Card } from './cards';
import { absoluteUrl, share } from './share';
import { toUrl } from '../state/url';
import { BRAND } from '../brand';
import { viewerRef } from './viewerRef';
import { track } from '../analytics';
import { TourPanel } from './TourPanel';
import { exitTour } from '../tours/tours';

export function CardPanel() {
  const sel = st.selected.value;
  const showCard = st.panel.value === 'card' && !!sel;
  const showTours = !showCard && (st.panel.value === 'tours' || !!st.tour.value);
  const open = showCard || showTours;
  const [cardState, setCard] = useState<{ id: string; card: Card | null } | null>(null);
  const body = useRef<HTMLDivElement>(null);
  const entry = sel ? BY_ID.get(sel.id) : null;

  // A card already in memory renders in the same pass as the panel (no loading flash).
  const peeked = sel ? peekCard(sel.id) : undefined;
  const card: Card | null | 'loading' = !sel ? 'loading' : peeked !== undefined ? peeked : cardState?.id === sel.id ? cardState.card : 'loading';

  useEffect(() => {
    if (!sel) return;
    let live = true;
    const id = sel.id;
    loadCard(id).then((c) => live && setCard({ id, card: c }));
    body.current?.scrollTo?.({ top: 0 });
    return () => {
      live = false;
    };
  }, [sel?.id]);

  // Drag the mobile sheet between peek / half / full.
  const drag = useRef<{ y: number; start: 'peek' | 'half' | 'full' } | null>(null);
  const onHandleDown = (e: PointerEvent) => {
    drag.current = { y: e.clientY, start: st.sheet.value };
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const onHandleUp = (e: PointerEvent) => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    const dy = e.clientY - d.y;
    const order = ['peek', 'half', 'full'] as const;
    let i = order.indexOf(d.start);
    if (Math.abs(dy) < 8) i = (i + 1) % 3;
    else if (dy < -40) i = Math.min(2, i + 1);
    else if (dy > 40) {
      if (i === 0) return st.select(null);
      i = i - 1;
    }
    st.sheet.value = order[i];
  };

  if (!open) return null;
  return (
    <section class="card-panel" data-sheet={st.sheet.value} aria-label={showTours ? 'Tours' : 'About this part'} data-testid="card-panel">
      <div class="sheet-handle" role="button" tabIndex={0} aria-label="Resize panel" onPointerDown={onHandleDown} onPointerUp={onHandleUp} onKeyDown={(e) => e.key === 'Enter' && (st.sheet.value = st.sheet.value === 'full' ? 'half' : 'full')} />
      <button type="button" class="btn ghost icon close" aria-label="Close" onClick={() => (showTours ? (st.tour.value ? exitTour() : (st.panel.value = sel ? 'card' : null)) : st.select(null))} data-testid="card-close">×</button>
      <div class="card-body" ref={body}>
        {showTours ? (
          <TourPanel />
        ) : entry && sel ? (
          <article class="card" data-testid="card" data-id={entry.id}>
            {st.tour.value && (
              <button type="button" class="btn small ghost back-to-tour" onClick={() => { st.select(null); st.panel.value = 'tours'; }} data-testid="back-to-tour">
                ← Back to tour
              </button>
            )}
            <p class="eyebrow">
              <span class="badge" style={{ '--c': LAYER_BY_ID[entry.layer].color }}>{LAYER_BY_ID[entry.layer].label}</span>
              {REGION_LABEL[entry.region] ?? ''}
            </p>
            <h1 class="card-title" data-testid="card-title">{card && card !== 'loading' ? card.title : entry.title}</h1>
            <p class="card-sub">
              {entry.name !== entry.title && <span>{entry.name}</span>}
              {sel.side && sel.side !== 'm' && entry.sides.length > 1 && <span> · {sideLabel(sel.side)} side</span>}
              {card && card !== 'loading' && card.latin && <em class="latin"> · {card.latin}</em>}
            </p>
            {card === 'loading' ? (
              <p class="muted" aria-busy="true">Loading…</p>
            ) : card ? (
              <CardContent card={card} />
            ) : (
              <div class="placeholder" data-testid="card-placeholder">
                <p>We haven’t written this one yet.</p>
                <p>
                  <a href={`https://en.wikipedia.org/wiki/Special:Search?search=${encodeURIComponent(entry.name)}`} target="_blank" rel="noopener">
                    Read about the {entry.name.toLowerCase()} on Wikipedia
                  </a>
                </p>
              </div>
            )}
            <CardActions id={entry.id} onExport={() => (st.exportOpen.value = true)} />
            <footer class="card-foot">
              <a href={`${BRAND.correctionsUrl}${encodeURIComponent(entry.title)}`} target="_blank" rel="noopener" data-testid="suggest-correction">Suggest a correction</a>
              <span>Not medical advice.</span>
            </footer>
          </article>
        ) : null}
      </div>
    </section>
  );
}

function CardContent({ card }: { card: Card }) {
  return (
    <div data-testid="card-content" data-kind={card.kind}>
      <p class="one-liner">{card.oneLiner}</p>
      {card.html.what_it_does && (
        <section>
          <h2>What it does</h2>
          <div dangerouslySetInnerHTML={{ __html: card.html.what_it_does }} />
        </section>
      )}
      {card.html.surprising_fact && (
        <section class="fact" data-testid="surprising-fact">
          <h2>Surprising fact</h2>
          <div dangerouslySetInnerHTML={{ __html: card.html.surprising_fact }} />
        </section>
      )}
      {card.html.why_it_matters && (
        <section>
          <h2>Why it matters</h2>
          <div dangerouslySetInnerHTML={{ __html: card.html.why_it_matters }} />
        </section>
      )}
      {card.kind === 'stub' && <p class="muted small">Short entry.</p>}
      {card.seeAlso.length > 0 && (
        <section class="see-also">
          <h2>Nearby</h2>
          <ul>
            {card.seeAlso.filter((id) => BY_ID.get(id)).map((id) => (
              <li key={id}>
                <a href={`/s/${id}`} data-testid="see-also" onClick={(e) => { e.preventDefault(); st.select(id); track('card_open', { structure_id: id, source: 'see-also' }); }}>
                  {BY_ID.get(id)!.title}
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}
      {card.links.length > 0 && (
        <section class="read-more">
          <h2>Read more</h2>
          <ul>
            {card.links.map((l) => (
              <li key={l.url}><a href={l.url} target="_blank" rel="noopener">{l.title}</a></li>
            ))}
          </ul>
        </section>
      )}
      {card.sources.length > 0 && (
        <details class="sources" data-testid="sources">
          <summary>Sources</summary>
          <ul>
            {card.sources.map((s, i) => (
              <li key={i}>{s.url ? <a href={s.url} target="_blank" rel="noopener">{s.cite}</a> : s.cite}</li>
            ))}
          </ul>
          <p class="muted small">
            {card.status === 'ai-drafted' ? 'Drafted with AI help; awaiting a human fact-check against these sources.' : card.status === 'ai-checked' ? 'Drafted with AI help and checked against these sources; awaiting human review.' : `Reviewed${card.reviewedBy ? ` by ${card.reviewedBy}` : ''}.`} Updated {card.updated}.
          </p>
        </details>
      )}
    </div>
  );
}

function CardActions({ id, onExport }: { id: string; onExport: () => void }) {
  const [copied, setCopied] = useState(false);
  const iso = st.isolate.value;
  const doShare = async () => {
    const url = absoluteUrl(toUrl({ selected: st.selected.value, camera: st.camera.value, layers: st.layers.value, hidden: st.hidden.value, isolate: st.isolate.value, body: st.body.value }));
    const r = await share({ url, title: `${BY_ID.get(id)?.title} — ${BRAND.name}`, text: BY_ID.get(id)?.oneLiner, structureId: id });
    if (r === 'copy') {
      setCopied(true);
      st.showToast('Link copied');
      setTimeout(() => setCopied(false), 2000);
    }
  };
  const isolateAs = (mode: 'alone' | 'skeleton' | 'region') => {
    st.isolateSelected(mode);
    track('isolate', { mode });
  };
  return (
    <div class="card-actions" role="toolbar" aria-label="Actions for this part">
      <button type="button" class={`btn small ${iso?.mode === 'alone' ? 'active' : ''}`} onClick={() => isolateAs('alone')} data-testid="action-isolate">Isolate</button>
      <button type="button" class={`btn small ${iso?.mode === 'skeleton' ? 'active' : ''}`} onClick={() => isolateAs('skeleton')} data-testid="action-isolate-skeleton">With skeleton</button>
      <button type="button" class={`btn small ${iso?.mode === 'region' ? 'active' : ''}`} onClick={() => isolateAs('region')} data-testid="action-isolate-region">Just this region</button>
      <button type="button" class="btn small" onClick={() => st.hideSelected()} data-testid="action-hide">Hide</button>
      <button type="button" class="btn small" onClick={() => viewerRef.current?.frame(id, { side: st.selected.value?.side })} data-testid="action-frame">Centre</button>
      {(iso || st.hidden.value.length > 0) && <button type="button" class="btn small" onClick={() => st.showAll()} data-testid="action-show-all">Show all</button>}
      {!st.classroom.value && <button type="button" class="btn small primary" onClick={doShare} data-testid="action-share">{copied ? 'Copied!' : 'Share'}</button>}
      <button type="button" class="btn small" onClick={onExport} data-testid="action-export">Save image</button>
    </div>
  );
}
